import { Assert } from "../../Assert";
import { PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyInt32, PropertyString } from "../../RadObject";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { NAMESPACE } from "./_Namespace";

import "./NumericUpDown.scss";

export type NumericUpDownPropertyValues =
    InputControlPropertyValues
    & {
        currencySymbol: string;
        precision: number;
        scale: number;
    };

export type NumericUpDownProperties = {
    [name in keyof NumericUpDownPropertyValues]: Property<NumericUpDownPropertyValues[name]>
};

export interface INumericUpDown extends IInputControl, NumericUpDownProperties
{
}

export class NumericUpDown extends InputControl implements INumericUpDown
{
    public static override classNamespace = NAMESPACE + "NumericUpDown";
    public static override className = "NumericUpDown";

    // #region Global Variables

    protected buttonContainerRef?: ElementRef;
    protected labelRef?: ElementRef;
    protected minusButtonRef?: ElementRef;
    protected plusButtonRef?: ElementRef;
    protected valueRef?: ElementRef;

    // #endregion

    // #region Properties

    public currencySymbol!: Property<string>;
    public precision!: Property<number>;
    public scale!: Property<number>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<NumericUpDownPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-numeric-up-down ui right labeled input");

        // Trigger Property Changes
        this.currencySymbol.triggerChange();
        this.precision.triggerChange();
        this.scale.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _currencySymbolChanged(change: PropertyChange<string>)
    {
        if (change.currentValue)
        {
            this.labelRef?.current.prependTo(this.$$element.get());
            this.labelRef?.current.text(change.currentValue);
        }
        else
            this.labelRef?.current.detach();
    }
    protected _elementValueChanged()
    {
        if (!this.__processElementValueChanged)
            return;

        this.value.set(this._elementValueGet());
    }
    protected _elementValueGet()
    {
        const
            value = this.valueRef?.current.val() ?? StringHelper.empty,
            valueAsFloat = parseFloat(value),
            valueValid = !isNaN(valueAsFloat) && /^-?[0-9]*\.?(?:[0-9]+)?$/gi.test(value);
            
        return valueValid ? valueAsFloat : null;
    }
    protected _elementValueSet(value: any)
    {
        this.__processElementValueChanged = false;
        this.valueRef?.current.val(this.valueToCorrectScaleString(value));
        this.__processElementValueChanged = true;
    }
    protected _precisionChanged(change: PropertyChange<number>)
    {
    }
    protected _scaleChanged(change: PropertyChange<number>)
    {
    }

    // #endregion

    // #region Private Methods

    private valueToCorrectScaleString(value: any)
    {
        if (typeof value !== "number")
            return value;

        const
            currencySymbol = this.currencySymbol.get(),
            scale = this.scale.get();

        return value.toFixed(currencySymbol ? 2 : (scale ?? 0));
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: NumericUpDownPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.currencySymbol = new PropertyString(this, propertyValues.currencySymbol, this._currencySymbolChanged);
        this.precision = new PropertyInt32(this, propertyValues.precision, this._precisionChanged);
        this.scale = new PropertyInt32(this, propertyValues.scale, this._scaleChanged);

        this.text.overrideGet(next =>
        {
            const
                value = this.value.get(),
                currencySymbol = this.currencySymbol.get() ?? StringHelper.empty;

            return value ? `${currencySymbol}${this.valueToCorrectScaleString(value)}` : value;
        });
        this.value.overrideSet((value, next) =>
        {
            this._elementValueSet(value);
            next(this._elementValueGet());
        });

        this.buttonContainerRef = new ElementRef();
        this.labelRef = new ElementRef();
        this.minusButtonRef = new ElementRef();
        this.plusButtonRef = new ElementRef();
        this.valueRef = new ElementRef();
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this.plusButtonRef?.current, "this.plusButtonRef?.current");
        Assert.isNotNullOrUndefined(this.minusButtonRef?.current, "this.minusButtonRef?.current");
        Assert.isNotNullOrUndefined(this.valueRef?.current, "this.valueElementRef?.current");

        this.plusButtonRef.current.on("click", () =>
        {
            const
                value = this.value.get();

            this.value.set(isNaN(value) || value === null ? 0 : value + 1);
        });

        this.minusButtonRef.current.on("click", () =>
        {
            const
                value = this.value.get();

            this.value.set(isNaN(value) || value === null ? 0 : value - 1);
        });

        this.valueRef.current.on("change input keyup", { self: this }, this.onInputValueChanged);
        this.valueRef.current.on("keydown", { self: this }, this.onInputValueKeyDown);
    }
    protected _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[]): JsxFragment
    {
        return (
            <div>
                <label ref={this.labelRef}  for={`${this.controlId}-input`} class="ui label">$</label>
                <input ref={this.valueRef} id={`${this.controlId}-input`} type="number" />
                <div ref={this.buttonContainerRef} class="rad-button-container ui mini vertical buttons">
                    <button ref={this.plusButtonRef} class="rad-button-plus ui icon button"><i class="up chevron icon"></i></button>
                    <button ref={this.minusButtonRef} class="rad-button-minus ui icon button"><i class="down chevron icon"></i></button>
                </div>
                {children}
            </div>
        );
    }

    // #endregion

    // #region Event Handlers

    protected onInputValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
    }
    protected onInputValueKeyDown(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self as this;

        if (evt.originalEvent.ctrlKey)
            return;

        const
            allowedCharacters = "0123456789.",
            allowedSpecialKeys = [
                "ArrowDown",
                "ArrowLeft",
                "ArrowRight",
                "ArrowUp",
                "Backspace",
                "Delete",
                "Home",
                "End",
                "Tab",
            ];

        if (allowedSpecialKeys.indexOf(evt.originalEvent.key) >= 0)
            return;

        const
            value = self.valueRef?.current.val() ?? StringHelper.empty;

        if (evt.originalEvent.key === "-" && (evt.originalEvent.target as HTMLInputElement).selectionStart === 0 && value.indexOf("-") === -1)
            return;

        if (allowedCharacters.indexOf(evt.originalEvent.key) >= 0 && !(evt.originalEvent.key === "." && value.indexOf(".") >= 0))
            return;

        evt.preventDefault();
    }

    // #endregion
}

